import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Loader2, ImageIcon } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Cookies from 'js-cookie';
import imageCompression from 'browser-image-compression';

interface LogosData {
    logo_tmp: string | null;
    image_profile: string | null;
    image_thumb: string | null;
}

type LogoType = 'logo' | 'profile' | 'cover';

const Logos: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<LogosData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [logoUploading, setLogoUploading] = useState<Record<LogoType, boolean>>({
        logo: false,
        profile: false,
        cover: false,
    });

    const CDN_BASE_URL = 'https://yellowslate.sgp1.cdn.digitaloceanspaces.com';

    useEffect(() => {
        fetchLogosData();
    }, [id]);

    const fetchLogosData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/media/${id}`);
            if (response.success && response.data && response.data.logos) {
                setData(response.data.logos);
            } else {
                setError(response.message || 'Failed to load logos information');
            }
        } catch (err: any) {
            console.error('Error fetching logos data:', err);
            setError('Failed to load logos information');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (type: LogoType, file: File) => {
        if (!file) return;

        try {
            setLogoUploading({ ...logoUploading, [type]: true });
            setError(null);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', type);

            // Get auth token from cookie
            const token = Cookies.get('crm_auth_token');

            // Build headers
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Use fetch directly for FormData upload (don't set Content-Type, browser will set it)
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${API_BASE_URL}/media/${id}/logo`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: formData,
            });

            const responseData = await response.json();

            if (response.ok && (responseData.success || responseData.status === 'success')) {
                showToast(`${type === 'logo' ? 'Logo' : type === 'profile' ? 'Profile picture' : 'Cover picture'} uploaded successfully!`, 'success');

                // Update only the specific logo in state instead of refetching all data
                if (data) {
                    const cdnUrl = responseData.data?.url || responseData.data?.filename;
                    setData({
                        ...data,
                        [type === 'logo' ? 'logo_tmp' : type === 'profile' ? 'image_profile' : 'image_thumb']: cdnUrl
                    });
                }
            } else {
                throw new Error(responseData.message || 'Failed to upload');
            }
        } catch (err: any) {
            console.error('Error uploading logo:', err);
            const errorMessage = err.message || 'Failed to upload image';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLogoUploading({ ...logoUploading, [type]: false });
        }
    };

    const handleLogoFileSelect = async (type: LogoType, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('Please select an image file', 'error');
            return;
        }

        // Define max file sizes in bytes for each type
        const maxFileSizesBytes: Record<LogoType, number> = {
            logo: 250 * 1024,      // 250KB
            profile: 1024 * 1024,  // 1MB
            cover: 1024 * 1024,    // 1MB
        };

        const maxSizeBytes = maxFileSizesBytes[type];
        let fileToUpload = file;

        // If file exceeds max size, compress it
        if (file.size > maxSizeBytes) {
            try {
                setLogoUploading({ ...logoUploading, [type]: true });
                showToast(`Compressing image from ${(file.size / 1024 / 1024).toFixed(2)}MB...`, 'info');

                const options = {
                    maxSizeMB: maxSizeBytes / (1024 * 1024),
                    maxWidthOrHeight: type === 'logo' ? 500 : 1200,
                    useWebWorker: true,
                    fileType: file.type as string,
                };

                fileToUpload = await imageCompression(file, options);

                const originalSizeKB = (file.size / 1024).toFixed(1);
                const compressedSizeKB = (fileToUpload.size / 1024).toFixed(1);
                showToast(`Image compressed: ${originalSizeKB}KB → ${compressedSizeKB}KB`, 'success');
            } catch (err) {
                console.error('Error compressing image:', err);
                showToast('Failed to compress image. Please try a smaller file.', 'error');
                setLogoUploading({ ...logoUploading, [type]: false });
                return;
            }
        }

        // Upload the (possibly compressed) file
        handleLogoUpload(type, fileToUpload);
    };

    const getImageUrl = (urlOrFilename: string | null) => {
        if (!urlOrFilename) return null;

        // If it's already a complete URL, return it as-is
        if (urlOrFilename.startsWith('http://') || urlOrFilename.startsWith('https://')) {
            return urlOrFilename;
        }

        // Otherwise, construct the URL (for backward compatibility with old data)
        return `${CDN_BASE_URL}/schools/${urlOrFilename}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div className="h-full bg-white dark:bg-gray-900">
            {/* Page Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <ImageIcon className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">School Logos</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Manage your school's logo, profile picture, and cover image</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            <div className="p-8 space-y-6">
                {/* School Logo */}
                <LogoUploadSection
                    title="School Logo"
                    description="Upload the main logo for the school"
                    type="logo"
                    currentImage={getImageUrl(data.logo_tmp)}
                    uploading={logoUploading.logo}
                    onFileSelect={(e) => handleLogoFileSelect('logo', e)}
                />

                {/* Profile Picture */}
                <LogoUploadSection
                    title="Profile Picture"
                    description="Upload a profile picture for the school"
                    type="profile"
                    currentImage={getImageUrl(data.image_profile)}
                    uploading={logoUploading.profile}
                    onFileSelect={(e) => handleLogoFileSelect('profile', e)}
                />

                {/* Cover Picture */}
                <LogoUploadSection
                    title="Cover Picture"
                    description="Upload a cover picture for the school"
                    type="cover"
                    currentImage={getImageUrl(data.image_thumb)}
                    uploading={logoUploading.cover}
                    onFileSelect={(e) => handleLogoFileSelect('cover', e)}
                />
            </div>
        </div>
    );
};

interface LogoUploadSectionProps {
    title: string;
    description: string;
    type: LogoType;
    currentImage: string | null;
    uploading: boolean;
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const LogoUploadSection: React.FC<LogoUploadSectionProps> = ({
    title,
    description,
    type,
    currentImage,
    uploading,
    onFileSelect,
}) => {
    // Define max file sizes for each type
    const maxFileSizes = {
        logo: '250KB',
        profile: '1MB',
        cover: '1MB',
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-brandColor px-6 py-3">
                <h2 className="text-lg font-semibold text-white">{title}</h2>
                <p className="text-xs text-white/80 mt-1">{description}</p>
            </div>
            <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Preview */}
                    <div className="flex-shrink-0">
                        <div className="w-48 h-48 border-2 border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                            {currentImage ? (
                                <img
                                    src={currentImage}
                                    alt={title}
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <div className="text-center p-4">
                                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No image uploaded</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload */}
                    <div className="flex-1 flex flex-col justify-center">
                        <label className="cursor-pointer">
                            <div className="flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin text-brandColor" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-5 h-5 mr-2 text-brandColor" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {currentImage ? 'Change Image' : 'Upload Image'}
                                        </span>
                                    </>
                                )}
                            </div>
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={onFileSelect}
                                disabled={uploading}
                            />
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Recommended: PNG or JPG. Max size: {maxFileSizes[type]}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            ✓ Large images will be automatically compressed
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Logos;
